import { useState } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';

import {
  PaymentInfoModal,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  countryToggleConfig,
  ListingCostsShape,
  MediaQuery
} from '@spotahome/ui-library';
import DateRangeFilter from '@spotahome/ui-library/src/tenant/components/Filters/DateRangeFilter';
import {
  isDateDisabledFactory,
  getMinAvailableDate
} from '@spotahome/ui-library/src/tenant/components/utils/listing-dates-validator-service';

import ListingContent from '../ListingContent';
import { usePricingContext } from '../../context/PricingContext';
import { landlordPoliciesContainFees } from '../../utils/landlordPolicies';
import { OPEN_PAYMENT_INFO_MODAL } from '../../channels';

import datesRangeShape from '../../shapes/datesRange';

import './ListingPaymentInfoModal.scss';

const ListingPaymentInfoModal = ({
  currencyIsoCode,
  countryCode,
  landlordPolicies = '',
  hasRentCollection = false,
  hasDepositProtection = false,
  listingCosts = null,
  onDatesChange,
  minStay,
  maxStay,
  availabilities = [],
  moveInDatesRanges = [],
  contractDurationTypesAllowed = ['temporal']
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  // isCalendarOpen is set inside onAfterOpen so DateRangeFilter only mounts
  // after the modal is fully visible — this ensures initiallyOpen works correctly.
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [localMoveIn, setLocalMoveIn] = useState('');
  const [localMoveOut, setLocalMoveOut] = useState('');

  const {
    pricingDetails: { pricing },
    downPaymentTimeInterval,
    detailedAdminFee,
    priceRange
  } = usePricingContext();

  const offer = pricing?.offer;

  const isMobile = !MediaQuery.isBiggerThanTablet();
  const minDate = getMinAvailableDate(moveInDatesRanges);
  const disableDayFnMoveIn = date => isDateDisabledFactory(moveInDatesRanges)(date);
  const disableDayFnMoveOut = date => isDateDisabledFactory(availabilities)(date);

  // PaymentInfoModal always closes itself before invoking onAddDatesClick
  // (see PaymentInfoModal.handleAddDatesClick → handleModalClose → onAddDatesClick).
  // If that internal contract ever changes, both modals would be open simultaneously
  // causing react-modal scroll-lock/focus-trap conflicts.
  const handleAddDatesClick = () => {
    setIsDatePickerOpen(true);
  };

  const isTemporalAllowed = contractDurationTypesAllowed.includes('temporal');

  const handleDatesChange = ({ moveIn: newMoveIn, moveOut: newMoveOut }) => {
    // Read the just-received values directly rather than relying on localMoveIn/localMoveOut,
    // which would be stale here since setState is async.
    const updatedMoveIn = newMoveIn !== undefined ? newMoveIn : localMoveIn;
    const updatedMoveOut = newMoveOut !== undefined ? newMoveOut : localMoveOut;

    if (newMoveIn !== undefined) setLocalMoveIn(newMoveIn || '');
    if (newMoveOut !== undefined) setLocalMoveOut(newMoveOut || '');

    if (isMobile) {
      const isComplete = isTemporalAllowed
        ? updatedMoveIn && updatedMoveOut
        : updatedMoveIn;

      if (isComplete) {
        onDatesChange({ moveIn: updatedMoveIn, moveOut: updatedMoveOut });
        setIsDatePickerOpen(false);
        setIsCalendarOpen(false);
        Mediator.publish(OPEN_PAYMENT_INFO_MODAL);
      }
    }
  };

  const handleApplyDates = () => {
    onDatesChange({ moveIn: localMoveIn, moveOut: localMoveOut });
    setIsDatePickerOpen(false);
    setIsCalendarOpen(false);
    Mediator.publish(OPEN_PAYMENT_INFO_MODAL);
  };

  const handleClearDates = () => {
    setLocalMoveIn('');
    setLocalMoveOut('');
  };

  const handleDatePickerClose = () => {
    setIsDatePickerOpen(false);
    setIsCalendarOpen(false);
  };

  const isApplyEnabled = isTemporalAllowed
    ? localMoveIn && localMoveOut
    : localMoveIn;

  const landlordPoliciesHasFees = landlordPoliciesContainFees(landlordPolicies);
  const moveLandlordPoliciesToPricing =
    countryToggleConfig(countryCode).moveLandlordPoliciesToPricing;

  const shouldRenderLandlordPolicies =
    moveLandlordPoliciesToPricing &&
    landlordPoliciesHasFees &&
    landlordPolicies;

  return (
    <>
      <PaymentInfoModal
        pricing={{
          ...pricing,
          listingCosts: pricing?.listingCosts || listingCosts,
          offer,
          currencyIsoCode,
          isSeasonalPricing: !!priceRange?.seasonalPrice
        }}
        downPaymentTimeInterval={downPaymentTimeInterval}
        detailedAdminFee={detailedAdminFee}
        contractDurationType={pricing && pricing.contractDurationType}
        hideDepositElanLaw={countryToggleConfig(countryCode).hideDepositElanLaw}
        hasDepositProtection={hasDepositProtection}
        onAddDatesClick={handleAddDatesClick}
        renderLandlordPolicies={
          shouldRenderLandlordPolicies
            ? () => (
                <ListingContent
                  className="listing-payment-info-modal__landlord-policies"
                  content={landlordPolicies}
                />
              )
            : null
        }
        hasRentCollection={hasRentCollection}
      />
      <Modal
        isOpen={isDatePickerOpen}
        onRequestClose={handleDatePickerClose}
        onAfterOpen={() => {
          setLocalMoveIn('');
          setLocalMoveOut('');
          setIsCalendarOpen(true);
        }}
        size={isMobile ? 'fullscreen' : 'big'}
        className="listing-payment-info-modal__datepicker-modal"
      >
        <ModalHeader
          label={trans('property_show.book_now_card.select_dates')}
          onRequestClose={handleDatePickerClose}
        />
        <ModalBody className="listing-payment-info-modal__datepicker-body">
          {isCalendarOpen && (
            <div className="listing-payment-info-modal__date-picker">
              <DateRangeFilter
                from={localMoveIn}
                to={localMoveOut}
                minStay={minStay}
                maxStay={maxStay}
                onDatesChange={handleDatesChange}
                initiallyOpen
                minDate={minDate}
                disableDayFnMoveIn={disableDayFnMoveIn}
                disableDayFnMoveOut={disableDayFnMoveOut}
                keepMoveOutCalendarOpen
                autoCompleteDates={false}
                numberOfMonths={2}
              />
            </div>
          )}
        </ModalBody>
        {!isMobile && (
          <ModalFooter className="listing-payment-info-modal__footer">
            <button
              type="button"
              className="listing-payment-info-modal__clear-btn"
              onClick={handleClearDates}
            >
              {trans('search.exposedfilters.dates.clear')}
            </button>
            <Button
              disabled={!isApplyEnabled}
              onClick={handleApplyDates}
            >
              {trans('property_show.book_now_card.apply_dates')}
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};

ListingPaymentInfoModal.propTypes = {
  currencyIsoCode: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  landlordPolicies: PropTypes.string,
  hasRentCollection: PropTypes.string,
  hasDepositProtection: PropTypes.bool,
  listingCosts: ListingCostsShape,
  onDatesChange: PropTypes.func.isRequired,
  minStay: PropTypes.number,
  maxStay: PropTypes.number,
  availabilities: PropTypes.arrayOf(PropTypes.shape(datesRangeShape)),
  moveInDatesRanges: PropTypes.arrayOf(PropTypes.shape(datesRangeShape)),
  contractDurationTypesAllowed: PropTypes.arrayOf(
    PropTypes.oneOf(['temporal', 'permanent'])
  )
};

export default ListingPaymentInfoModal;
